import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "status"]
  static values = {
    url: String,
    templateId: Number,
    admin: { type: Boolean, default: false },
    // i18n strings
    loading: { type: String, default: "Loading..." },
    success: { type: String, default: "Data copied from" },
    noData: { type: String, default: "No previous timesheet found" },
    error: { type: String, default: "Error loading data" },
    multipleDisabled: { type: String, default: "Nur bei einzelnem Mitarbeiter verfügbar" },
  }

  connect() {
    // Check initial state of employee selection
    this.toggleButton()
  }

  toggleButton() {
    if (!this.hasButtonTarget) return

    const form = this.element.closest("form") || document.querySelector("form")
    if (!form) return

    // Find multi-select employee field
    const employeeSelect = form.querySelector('select[name*="[employee_ids]"]')

    if (employeeSelect) {
      const selectedOptions = Array.from(employeeSelect.selectedOptions)
      const isMultiple = selectedOptions.length > 1

      if (isMultiple) {
        this.buttonTarget.disabled = true
        this.buttonTarget.classList.add("opacity-50", "cursor-not-allowed")
        this.buttonTarget.title = this.multipleDisabledValue
      } else {
        this.buttonTarget.disabled = false
        this.buttonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        this.buttonTarget.title = ""
      }
    }
  }

  async fetch() {
    const button = this.buttonTarget
    const originalText = button.innerHTML

    // Show loading state
    button.disabled = true
    button.innerHTML = `
      <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      ${this.loadingValue}
    `

    try {
      const params = this.buildParams()
      const response = await fetch(`${this.urlValue}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      })

      const data = await response.json()

      if (data.available) {
        this.fillForm(data.data)
        this.showStatus("success", `${this.successValue} ${this.formatDate(data.previous_date)}`)
      } else {
        this.showStatus("info", this.noDataValue)
      }
    } catch (error) {
      console.error("Error fetching previous day data:", error)
      this.showStatus("error", this.errorValue)
    } finally {
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  buildParams() {
    const params = new URLSearchParams()
    params.append("template_id", this.templateIdValue)

    // Get date from form
    const dateInput = this.findFormInput("reported_date")
    if (dateInput) {
      params.append("date", dateInput.value)
    }

    // Include employee_id from either employee_ids multi-select or employee_id input
    const form = this.element.closest("form") || document.querySelector("form")

    // Try employee_ids multi-select first
    const employeeIdsSelect = form?.querySelector('select[name*="[employee_ids]"]')
    if (employeeIdsSelect && employeeIdsSelect.selectedOptions.length === 1) {
      params.append("employee_id", employeeIdsSelect.selectedOptions[0].value)
    } else {
      // Fallback to employee_id
      const employeeInput = this.findFormInput("employee_id")
      if (employeeInput) {
        params.append("employee_id", employeeInput.value)
      }
    }

    return params
  }

  fillForm(data) {
    // Fill time fields
    this.setTimeField("time_from", data.time_from)
    this.setTimeField("time_to", data.time_to)
    this.setTimeField("time_break", data.time_break)

    // Fill other basic fields
    this.setSelectField("site_id", data.site_id)
    this.setNumberField("hourly_wage", data.hourly_wage)

    // Fill task_ids (multi-select)
    this.setMultiSelectField("task_ids", data.task_ids)

    // Fill custom fields
    if (data.custom_field_data) {
      Object.entries(data.custom_field_data).forEach(([key, value]) => {
        this.setCustomField(key, value)
      })
    }

    // Fill allowances
    if (data.allowance_data) {
      Object.entries(data.allowance_data).forEach(([key, values]) => {
        this.setAllowanceField(key, values)
      })
    }

    // Note: Nested times (driving_times, repair_times, workshop_times) are complex
    // and would require dynamically creating form rows. Skipping for now as it
    // requires interaction with the nested_form_controller.
  }

  // Scoped to the form-level `configurable_time_sheet[...]` inputs on purpose. A bare
  // `[name*="[site_id]"]` also matches `site_allocations[0][site_id]`, and a multi-site
  // template does not render the form-level site select at all — so the unscoped lookup
  // wrote the previous day's Baustelle into the first Baustellen-Zuordnung row instead.
  // SlimSelect never reflects such a write, leaving a submitted site the user can neither
  // see nor clear. Allocation rows carry no copyable values anyway (copyable_attributes
  // has no `hours`), so they are simply out of scope here.
  findFormInput(fieldName) {
    const form = this.element.closest("form") || document.querySelector("form")
    if (!form) return null

    // Try different naming patterns
    return (
      form.querySelector(`[name^="configurable_time_sheet["][name*="[${fieldName}]"]`) ||
      form.querySelector(`[name="${fieldName}"]`) ||
      form.querySelector(`#configurable_time_sheet_${fieldName}`)
    )
  }

  // The select may be wrapped by SlimSelect, which does not observe programmatic
  // `.value` writes — the visible box would keep the old label while the native select
  // submits the new value. select_controller#setSelected drives the SlimSelect store
  // first and exists for exactly this case.
  selectControllerFor(element) {
    return this.application.getControllerForElementAndIdentifier(element, "select")
  }

  setTimeField(fieldName, value) {
    if (!value) return

    const input = this.findFormInput(fieldName)
    if (input) {
      // Parse time from ISO format (2000-01-01T08:00:00.000Z) or time string
      let timeStr = value
      if (value.includes("T")) {
        const date = new Date(value)
        timeStr = `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`
      }
      input.value = timeStr

      // Trigger change event for any listeners
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  setSelectField(fieldName, value) {
    if (!value) return

    const input = this.findFormInput(fieldName)
    if (!input) return

    const select = this.selectControllerFor(input)
    if (select) {
      select.setSelected(String(value))
      return
    }

    input.value = value
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  setNumberField(fieldName, value) {
    if (value === null || value === undefined) return

    const input = this.findFormInput(fieldName)
    if (input) {
      input.value = value
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  setMultiSelectField(fieldName, values) {
    if (!values || !Array.isArray(values)) return

    const form = this.element.closest("form") || document.querySelector("form")
    if (!form) return

    // Handle both checkboxes and multi-select. Scoped to the form-level fields for the
    // same reason as #findFormInput — `[name*="[task_ids]"]` otherwise matches an
    // allocation row's `site_allocations[0][task_ids][]`.
    const scope = '[name^="configurable_time_sheet["]'
    const checkboxes = form.querySelectorAll(`${scope}[name*="[${fieldName}][]"][type="checkbox"]`)
    if (checkboxes.length > 0) {
      checkboxes.forEach((checkbox) => {
        checkbox.checked = values.includes(parseInt(checkbox.value))
        checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      })
    }

    const select = form.querySelector(`select${scope}[name*="[${fieldName}]"]`)
    if (select && select.multiple) {
      Array.from(select.options).forEach((option) => {
        option.selected = values.includes(parseInt(option.value))
      })
      // Re-read the options into SlimSelect rather than #setSelected: that helper
      // assigns a scalar to `.value`, which on a multiple select matches no option and
      // would drop the selection we just made.
      this.selectControllerFor(select)?.refresh()
      select.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  setCustomField(key, value) {
    const form = this.element.closest("form") || document.querySelector("form")
    if (!form) return

    const input = form.querySelector(`[name="custom_fields[${key}]"]`)
    if (input) {
      if (input.type === "checkbox") {
        input.checked = value === true || value === "1" || value === "true"
      } else {
        input.value = value
      }
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  setAllowanceField(key, values) {
    const form = this.element.closest("form") || document.querySelector("form")
    if (!form) return

    if (values.input !== undefined && values.input !== null) {
      const inputField = form.querySelector(`[name="allowances[${key}][input]"]`)
      if (inputField) {
        if (inputField.type === "checkbox") {
          inputField.checked =
            values.input === true || values.input === "1" || values.input === "true"
        } else {
          inputField.value = values.input
        }
        inputField.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    if (values.whole_day !== undefined) {
      const wholeDayField = form.querySelector(`[name="allowances[${key}][whole_day]"]`)
      if (wholeDayField) {
        wholeDayField.checked = values.whole_day === true
        wholeDayField.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
  }

  showStatus(type, message) {
    const statusEl = this.statusTarget
    statusEl.classList.remove("hidden", "text-green-600", "text-blue-600", "text-red-600")

    switch (type) {
      case "success":
        statusEl.classList.add("text-green-600")
        break
      case "info":
        statusEl.classList.add("text-blue-600")
        break
      case "error":
        statusEl.classList.add("text-red-600")
        break
    }

    statusEl.textContent = message

    // Hide after 5 seconds
    setTimeout(() => {
      statusEl.classList.add("hidden")
    }, 5000)
  }

  formatDate(dateStr) {
    if (!dateStr) return ""
    const date = new Date(dateStr)
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }
}
